import { drawing as d } from '@progress/kendo-drawing';
import { deepExtend } from './../utils/deep-extend';
import { Element } from './Element';
import { TRANSPARENT } from '../constants';
import { getColor, normalizeDrawingOptions } from './utils';
const defaultOptions = {
    stroke: {
        color: 'gray',
        width: 1
    },
    fill: {
        color: TRANSPARENT
    }
};
/**
 * Base class for visual elements that can be rendered with fill and stroke properties.
 * Extends Element and provides common functionality for visual styling and drawing operations.
 */
export class VisualBase extends Element {
    /**
     * Creates a new VisualBase instance.
     * @param options Configuration options for the visual element
     */
    constructor(options) {
        options = deepExtend({}, defaultOptions, options);
        super(options);
        options = this.options;
        options.fill = normalizeDrawingOptions(options.fill);
        options.stroke = normalizeDrawingOptions(options.stroke);
    }
    /**
     * Sets the fill color and opacity of the visual element.
     * @param color The fill color
     * @param opacity The fill opacity (optional)
     */
    fill(color, opacity) {
        this._fill({
            color: getColor(color),
            opacity: opacity
        });
    }
    /**
     * Sets the stroke color, width, and opacity of the visual element.
     * @param color The stroke color
     * @param width The stroke width
     * @param opacity The stroke opacity
     */
    stroke(color, width, opacity) {
        this._stroke({
            color: getColor(color),
            width: width,
            opacity: opacity
        });
    }
    /**
     * Redraws the visual element with updated options.
     * @param options New options to apply to the visual element
     */
    redraw(options) {
        if (options) {
            const stroke = options.stroke;
            const fill = options.fill;
            if (stroke) {
                this._stroke(normalizeDrawingOptions(stroke));
            }
            if (fill) {
                this._fill(normalizeDrawingOptions(fill));
            }
            super.redraw(options);
        }
    }
    /** @hidden */
    _hover(show) {
        const options = this.options;
        const hover = options.hover;
        if (hover && hover.fill) {
            const fill = show ? normalizeDrawingOptions(hover.fill) : options.fill;
            this._setFill(fill);
        }
    }
    /** @hidden */
    _setFill(fill) {
        this.drawingElement.fill(fill.color, fill.opacity);
    }
    /** @hidden */
    _evalStrokeOptions(strokeOptions) {
        const options = this.options;
        deepExtend(options, {
            stroke: strokeOptions
        });
        strokeOptions = options.stroke;
        let stroke = null;
        if (strokeOptions.width > 0) {
            stroke = {
                color: strokeOptions.color,
                width: strokeOptions.width,
                opacity: strokeOptions.opacity,
                dashType: strokeOptions.dashType
            };
        }
        return stroke;
    }
    /** @hidden */
    _evalFillOptions(fillOptions) {
        const options = this.options;
        deepExtend(options, {
            fill: fillOptions || {}
        });
        return options.fill;
    }
    /** @hidden */
    _stroke(strokeOptions) {
        const stroke = this._evalStrokeOptions(strokeOptions);
        this.drawingElement.options.set('stroke', stroke);
    }
    /** @hidden */
    _fill(fillOptions) {
        const fill = this._evalFillOptions(fillOptions);
        if (fill.gradient) {
            const gradient = fill.gradient;
            const GradientClass = (gradient.type === 'radial' ? d.RadialGradient : d.LinearGradient);
            this.drawingElement.fill(new GradientClass(gradient));
        }
        else {
            this.drawingElement.fill(fill.color, fill.opacity);
        }
    }
}
